// src/contexts/AccessContext.jsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { supabase, hasSupabase } from "../supabaseClient";
import { useAuth } from "./AuthContext";
import { getUserEntitlements } from "../lib/entitlementsCache";

const AccessContext = createContext(null);
const MAX_FAILURES = 5;

function ceilDaysBetween(nowMs, futureMs) {
  const diff = futureMs - nowMs;
  if (diff <= 0) return 0;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function parseTs(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function AccessProvider({ children }) {
  const { isAuthReady, session: authSession, user: authUser } = useAuth() || {};
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [userRow, setUserRow] = useState(null);
  const [error, setError] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // --- flood control / stability ---
  const inflightRef = useRef(null);
  const lastFetchAtRef = useRef(0);
  const failCountRef = useRef(0);
  const pollJitterMsRef = useRef(Math.floor(Math.random() * 20000));
  const lockoutRef = useRef(false);
  const lastWarnKeyRef = useRef("");
  const lastDebugRef = useRef(""); // prevents dev log spam
  const userRowRef = useRef(null); // stable reference for refreshAccess

  const warnOnce = useCallback((key, ...args) => {
    if (lastWarnKeyRef.current === key) return;
    lastWarnKeyRef.current = key;
    console.warn(...args);
  }, []);

  // Use centralized entitlementsCache to deduplicate queries
  const fetchUsersPublic = useCallback(async (userId) => {
    if (!userId) return null;
    if (!hasSupabase()) {
      // env missing: don't throw, just return null
      return null;
    }

    return getUserEntitlements(supabase, userId);
  }, []);

  const refreshAccess = useCallback(
    async (opts = {}) => {
      const { force = false } = opts;

      // Guard: Treat “auth not ready” as loading (not locked)
      if (!isAuthReady && !force) {
        setLoading(true);
        return null;
      }

      // If supabase client isn't configured, stop immediately (prevents storms)
      if (!hasSupabase()) {
        warnOnce(
          "SUPABASE_MISSING",
          "[AccessContext] Supabase client is null. Check VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY and restart Vite."
        );
        setError(new Error("SUPABASE_NOT_CONFIGURED"));
        setSession(null);
        setUserRow(null);
        setLoading(false);
        return null;
      }

      // Halt if we've hit lockout due to repeated failures unless caller forces a retry
      if (lockoutRef.current && !force) {
        warnOnce(
          "ACCESS_LOCKED_OUT",
          "[AccessContext] refreshAccess blocked after max failures; awaiting manual retry/auth change"
        );
        return null;
      }
      if (lockoutRef.current && force) {
        lockoutRef.current = false;
        failCountRef.current = 0;
      }

      // single-flight: reuse in-flight refresh to prevent parallel spam
      if (inflightRef.current) return inflightRef.current;

      // cooldown: avoid rapid re-calls
      const now = Date.now();
      const minGapMs = 1500;
      if (!force && now - lastFetchAtRef.current < minGapMs) {
        return userRowRef.current;
      }
      lastFetchAtRef.current = now;

      // backoff on repeated failures (caps at 30s)
      const backoffMs = Math.min(30000, failCountRef.current * failCountRef.current * 500);
      if (!force && backoffMs > 0) {
        // If we’re failing repeatedly, let it cool down
        // (Note: lastFetchAtRef.current already set; this is fine)
      }

      const p = (async () => {
        try {
          setError(null);
          setLoading(true);

          // IMPORTANT: do NOT call supabase.auth.getSession() here.
          // AccessContext should consume AuthContext state only to avoid auth request storms.
          const sess = authSession ?? null;
          setSession(sess);

          const userId = authUser?.id || sess?.user?.id;
          if (!userId) {
            setUserRow(null);
            failCountRef.current = 0;
            lockoutRef.current = false;
            lastWarnKeyRef.current = "";
            return null;
          }

          const row = await fetchUsersPublic(userId);
          setUserRow(row);
          userRowRef.current = row;

          if (import.meta.env.DEV) {
            console.log("[AccessContext] Fetched users_public row:", {
              userId: userId.slice(0, 8) + "...",
              access_until: row?.userPublic?.access_until,
              trial_ends_at: row?.userPublic?.trial_ends_at,
            });
          }

          failCountRef.current = 0;
          lockoutRef.current = false;
          lastWarnKeyRef.current = ""; // allow future warnings after recovery
          return row;
        } catch (e) {
          failCountRef.current = Math.min(20, failCountRef.current + 1);

          if (failCountRef.current >= MAX_FAILURES) {
            lockoutRef.current = true;
            console.error(
              "[AccessContext] refreshAccess reached max consecutive failures; locking access",
              { failCount: failCountRef.current }
            );
            setUserRow(null);
            userRowRef.current = null;
            setError(e);
            return null;
          }

          // avoid logging the same failure endlessly
          warnOnce(
            "ACCESS_REFRESH_FAILED",
            "[AccessContext] refreshAccess failed (backing off)",
            { failCount: failCountRef.current, message: e?.message || String(e) }
          );

          setError(e);
          // keep old userRow (don’t nuke it) to prevent UI thrash
          return null;
        } finally {
          setLoading(false);
          inflightRef.current = null;
        }
      })();

      inflightRef.current = p;
      return p;
    },
    [fetchUsersPublic, warnOnce, isAuthReady, authSession, authUser?.id]
  );

  useEffect(() => {
    userRowRef.current = userRow;
  }, [userRow]);

  // Track online/offline state
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      refreshAccess({ force: true }).catch(() => {});
    };
    const handleOffline = () => {
      setIsOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [refreshAccess]);

  const computed = useMemo(() => {
    const now = new Date();

    // fetchUsersPublic returns {userPublic, subscription}, so access nested structure
    const trialEnds = parseTs(userRow?.userPublic?.trial_ends_at);
    const accessUntil = parseTs(userRow?.userPublic?.access_until);
    
    // FIX: Check subscription status and use current_period_end as fallback
    const subscription = userRow?.subscription;
    const subscriptionStatus = subscription?.status;
    const currentPeriodEnd = subscription?.current_period_end 
      ? parseTs(subscription.current_period_end) 
      : null;

    let effectiveUntil = null;
    if (trialEnds && accessUntil) {
      effectiveUntil = new Date(Math.max(trialEnds.getTime(), accessUntil.getTime()));
    } else {
      effectiveUntil = accessUntil || trialEnds;
    }
    
    // FIX: If subscription is active but effectiveUntil is missing/broken, use current_period_end
    if (subscriptionStatus === "active" && currentPeriodEnd) {
      if (!effectiveUntil || currentPeriodEnd.getTime() > effectiveUntil.getTime()) {
        effectiveUntil = currentPeriodEnd;
      }
    }

    const isLifetime =
      effectiveUntil && effectiveUntil.getTime() - now.getTime() > 1000 * 60 * 60 * 24 * 365 * 10;

    // FIX: Allow access if subscription is active, even if effectiveUntil math is broken
    const hasActiveSubscription = subscriptionStatus === "active" && currentPeriodEnd && currentPeriodEnd.getTime() > now.getTime();
    const hasValidAccess = !!effectiveUntil && effectiveUntil.getTime() > now.getTime();
    const isUnlocked = hasActiveSubscription || hasValidAccess;

    const daysRemaining = effectiveUntil
      ? ceilDaysBetween(now.getTime(), effectiveUntil.getTime())
      : 0;

    const result = {
      now,
      trialEnds,
      accessUntil,
      effectiveUntil,
      isUnlocked,
      isLifetime,
      daysRemaining,
    };
    // Cache last known unlock state for offline mode in localStorage
    if (isUnlocked) {
      try {
        localStorage.setItem("mudo:lastUnlocked", "1");
      } catch {}
    }
    // Dev log without spam: only log when the “signature” changes
    if (import.meta.env.DEV) {
      const sig = `${result.isUnlocked}|${result.daysRemaining}|${result.effectiveUntil?.toISOString?.() || "null"}|${subscriptionStatus}`;
      if (lastDebugRef.current !== sig) {
        lastDebugRef.current = sig;
        console.log("[Access] TopBar badge computation:", {
          userRowLoaded: !!userRow,
          raw_access_until: userRow?.userPublic?.access_until,
          raw_access_until_type: typeof userRow?.userPublic?.access_until,
          raw_trial_ends_at: userRow?.userPublic?.trial_ends_at,
          raw_current_period_end: subscription?.current_period_end,
          raw_current_period_end_type: typeof subscription?.current_period_end,
          subscription_status: subscriptionStatus,
          parsed_accessUntil: accessUntil?.toISOString?.(),
          parsed_trialEnds: trialEnds?.toISOString?.(),
          parsed_currentPeriodEnd: currentPeriodEnd?.toISOString?.(),
          effectiveUntil: result.effectiveUntil?.toISOString?.(),
          hasActiveSubscription,
          hasValidAccess,
          isUnlocked: result.isUnlocked,
          daysRemaining: result.daysRemaining,
          now_iso: now.toISOString(),
          now_ms: now.getTime(),
        });
      }
    }

    return result;
  }, [userRow]);

  // Boot + auth changes (consume AuthContext only)
  useEffect(() => {
    if (!isAuthReady) {
      setLoading(true);
      return;
    }

    const sess = authSession ?? null;
    setSession(sess);

    const userId = authUser?.id || sess?.user?.id;
    if (!userId) {
      setUserRow(null);
      userRowRef.current = null;
      setLoading(false);
      // Clear cached unlock state on logout
      try {
        localStorage.removeItem("mudo:lastUnlocked");
      } catch {}
      return;
    }

    refreshAccess({ force: true }).catch(() => {});
  }, [isAuthReady, authSession?.expires_at, authUser?.id, refreshAccess]);

  // Auto-refresh every 90s while locked — but ONLY if we have supabase and aren’t in error backoff
  useEffect(() => {
    if (!hasSupabase()) return;
    if (loading) return;
    if (computed.isUnlocked) return;
    if (!session?.user?.id) return;
    if (lockoutRef.current) return;

    const BASE_POLL_MS = 90_000;
    const pollMs = BASE_POLL_MS + pollJitterMsRef.current;
    const interval = setInterval(() => {
      // don’t pile on if failing repeatedly
      const backoffMs = Math.min(30000, failCountRef.current * failCountRef.current * 500);
      const now = Date.now();
      if (backoffMs > 0 && now - lastFetchAtRef.current < backoffMs) return;
      if (lockoutRef.current) return;

      refreshAccess().catch(() => {});
    }, pollMs);

    return () => clearInterval(interval);
  }, [loading, computed.isUnlocked, refreshAccess, session?.user?.id]);

  const value = useMemo(() => {
    return {
      loading,
      error,
      session,
      userRow,
      ...computed,
      refreshAccess,
      isOffline,
      accessLabel: computed.isLifetime
        ? "Lifetime"
        : computed.isUnlocked
          ? `${computed.daysRemaining} days`
          : "Expired",
    };
  }, [loading, error, session, userRow, computed, refreshAccess, isOffline]);

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

export function useAccess() {
  const ctx = useContext(AccessContext);
  if (!ctx) throw new Error("useAccess must be used within an AccessProvider");
  return ctx;
}
